// import { Separator } from "@/components/atoms/divider/separator";
import ListOfEditableOverview from "@/components/organisms/ListOfEditableOverview";
import { useState, useEffect, useMemo } from "react";
import Filter from "@/components/organisms/Filter";
import { useAuthContext } from '@/context/AuthContext';
import useConsultantStatementYears from "@/services/admin-services/query/useConsultantStatementYears";
import useConsultantStatement from "@/services/admin-services/query/useConsultantStatement";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import SkeletonHandler from "@/components/templates/skeletons/SkeletonHandler";
import { Toaster } from "@/components/organisms/feedback/toaster";
import AdsClickIcon from "@mui/icons-material/AdsClick";
import { Label } from "@/components/atoms/texts/label";
import { useParams } from "react-router-dom";
import PrintEmptyDataMessage from "@/components/molecules/PrintEmptyDataMessage";
import PrintErrorMessage from "@/components/organisms/feedback/PrintErrorMessage";
import InvoicesTab from "./InvoicesTab";
import { Button } from "@/components/molecules/buttons/button";
import { Paid } from '@mui/icons-material';
import { Separator } from "@/components/atoms/divider/separatorShadcn";
import StatementTable from "@/components/organisms/StatementTable";
import StatementOperationsChart from "@/components/organisms/charts/StatementOperationsChart";
import { useAppSelector } from '@/store/hooks.js';
import InterestsHoldActiveWarning from "@/components/organisms/feedback/InterestsHoldActiveWarning";
import { useStatementInterestsHolds } from "@/hooks/global-hooks/useStatementInterestsHolds";
import { RefreshCw, BadgePercent } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/organisms/feedback/use-toast';
import useRefreshStatement from '@/services/admin-services/mutation/useRefreshStatement';
import useProcessOverflowInterests from '@/services/admin-services/mutation/useProcessOverflowInterests';
import RefreshStatementDialog from '@/components/organisms/RefreshStatementDialog';
import ProcessOverflowInterestsDialog from '@/components/organisms/ProcessOverflowInterestsDialog';

const MONTH_KEYS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];

export default function StatementTab() {
    const { id } = useParams();
    const { hasPrivileges } = useAuthContext();
    const user = useAppSelector((state) => state.auth.currentUser);
    const holdsState = useStatementInterestsHolds({ consultantUuid: id });

    // ✅ Calculer l'année par défaut : la plus récente disponible
    const { allYears, errorYear } = useConsultantStatementYears(id);

    const defaultYear = useMemo(() => {
        if (!allYears || allYears.length === 0) return null;

        // Trier par ordre décroissant et prendre la plus récente
        const sortedYearsDesc = [...allYears].sort((a, b) => b - a);
        return sortedYearsDesc[0]; // Année la plus récente
    }, [allYears]);

    const [selectedYear, setSelectedYear] = useState(null);
    const [invoicesTab, setInvoiceTab] = useState(false);
    const [t] = useTranslation('global');
    const { toast } = useToast();
    const canManageStatement = hasPrivileges("ADMIN_STATEMENT");
    const { refreshStatement } = useRefreshStatement();
    const { processOverflowInterests, loading: processInterestsLoading, error: processInterestsError } = useProcessOverflowInterests();
    const [openRefreshDialog, setOpenRefreshDialog] = useState(false);
    const [openInterestsDialog, setOpenInterestsDialog] = useState(false);
    const [refreshProgress, setRefreshProgress] = useState(null);

    // ✅ Synchroniser selectedYear avec allYears et gérer la persistance
    useEffect(() => {
        if (!allYears || allYears.length === 0) return;

        // Si selectedYear existe déjà ET est toujours dans allYears
        if (selectedYear && allYears.includes(selectedYear)) {
            return; // Persistance de l'année lors du changement de consultant
        }

        // Sinon on initialise avec l'année par défaut
        if (defaultYear) {
            setSelectedYear(defaultYear);
        }
    }, [allYears, defaultYear, id]); // id ajouté pour détecter le changement de consultant

    // ✅ Skip la requête tant que selectedYear n'est pas défini
    const { error, loading, data: yearData, refetch: refetchStatement } = useConsultantStatement(selectedYear, id, !selectedYear);

    const getMonthLabel = (month) => t(`global.months.${MONTH_KEYS[month - 1]}`);

    // Les mois sont rafraichis un par un, dans l'ordre, car le solde d'un mois depend du precedent.
    const handleRefreshMonths = async (months) => {
        const failedMonths = [];
        let lastErrorMessage = null;

        for (const [index, month] of months.entries()) {
            setRefreshProgress({ current: index + 1, total: months.length });
            try {
                await refreshStatement(id, selectedYear, month);
            } catch (err) {
                failedMonths.push(month);
                lastErrorMessage = err.message;
            }
        }
        setRefreshProgress(null);

        try {
            await refetchStatement();
        } catch (err) {
            console.debug('Statement refetch error:', err);
        }

        if (failedMonths.length === 0) {
            setOpenRefreshDialog(false);
            toast({
                title: t('consultants.statement.refresh.successTitle'),
                description: t('consultants.statement.refresh.successDescription', { count: months.length }),
                variant: 'success',
            });
            return;
        }

        toast({
            title: t('consultants.statement.refresh.errorTitle'),
            description: t('consultants.statement.refresh.errorDescription', {
                months: failedMonths.map(getMonthLabel).join(', '),
                message: lastErrorMessage,
            }),
            variant: 'error',
        });
    };

    const handleProcessOverflowInterests = async (year, month) => {
        try {
            const result = await processOverflowInterests(id, year, month);
            if (!result?.data?.processOverflowInterests) {
                throw new Error(t('consultants.statement.overflowInterests.notProcessed'));
            }
            await refetchStatement();
            setOpenInterestsDialog(false);
            toast({
                title: t('consultants.statement.overflowInterests.successTitle'),
                description: t('consultants.statement.overflowInterests.successDescription'),
                variant: 'success',
            });
        } catch (err) {
            toast({
                title: t('consultants.statement.overflowInterests.errorTitle'),
                description: err.message,
                variant: 'error',
            });
        }
    };
    // const [allYearlyData, setAllYearlyData] = useState([]);
    // const [yearData, setYearData] = useState({});

    // //faire une requête pour chaque années disponible dans allYears
    // useEffect(() => {
    //     if (id && allYears) {
    //         const fetchData = async () => {
    //             const data = [];
    //             for (const year of allYears) {
    //                 const result = await getAllStatamentData(user.strongToken, year, id);
    //                 if (result.data) {
    //                     data.push(result.data);
    //                 }
    //             }
    //             setAllYearlyData(data);
    //         };
    //         fetchData();
    //     }
    // }, [id, allYears]);

    // useEffect(() => {
    //     // Sélectionner les données de l'année cliquée
    //     if (allYearlyData) {
    //         const selectedData = allYearlyData.find(
    //             (item) => item.year === parseInt(selectedYear)
    //         );
    //         // console.log("selected data", selectedData);
    //         setYearData(selectedData);
    //     }
    // }, [selectedYear, allYearlyData]);

    if (!allYears) {
        return (
            <div className="grid grid-cols-2">
                <SkeletonHandler name={"overview"} />
                <SkeletonHandler name={"overflow"} />
            </div>
        );
    }

    if (id == "undefined")
        return (
            <PrintEmptyDataMessage
                message={"error.selectUser"}
                icon={<AdsClickIcon />}
            />
        );

    if (error) {
        console.log("Statement Tab error : ", error);
        <PrintErrorMessage message={error} />;
    }

    return (
        !invoicesTab ?
            <div className="h-[100%] w-[100%] flex flex-col gap-1 statementTab ">



                {allYears?.length > 0 && <div className="flex flex-row w-full justify-end h-[47px]">
                    <div className="ml-auto flex items-center justify-center w-full  space-x-4">
                        <Filter
                            items={allYears && allYears}
                            informSelectedItem={setSelectedYear}
                            defaultSelectedItem={selectedYear || defaultYear}
                            sortItemDESC={true}
                        />

                    </div>
                    <div className="flex  gap-firstDegree">
                        {canManageStatement && selectedYear && (
                            <>
                                <Button
                                    style="ghost"
                                    showLeftIcon
                                    leftIcon={<RefreshCw className="h-5 w-5" />}
                                    showText={false}
                                    showToolTip
                                    sizePadding={"firstDegree"}
                                    text="consultants.statement.refresh.button"
                                    onClick={() => setOpenRefreshDialog(true)}
                                    disabled={Boolean(refreshProgress)}
                                />
                                <Button
                                    style="ghost"
                                    showLeftIcon
                                    leftIcon={<BadgePercent className="h-5 w-5" />}
                                    showText={false}
                                    showToolTip
                                    sizePadding={"firstDegree"}
                                    text="consultants.statement.overflowInterests.button"
                                    onClick={() => setOpenInterestsDialog(true)}
                                    disabled={processInterestsLoading}
                                />
                            </>
                        )}
                        {hasPrivileges("ADMIN_INVOICE") && <Button onClick={() => setInvoiceTab(true)} leftIcon={<Paid />} showLeftIcon showToolTip text="consultants.statement.overflow.seeBillsDetails" />}
                    </div>
                </div>}

                {allYears?.length == 0 ? (
                    <PrintEmptyDataMessage
                        message={"error.emptyData"}
                        icon={<WarningAmberRoundedIcon />}
                    />
                ) : error ? (
                    <PrintErrorMessage error={error} />
                ) : (
                    <div className=" flex  gap-secondDegree ">
                        <div className=" w-[300px] flex flex-col gap-firstDegree  ">
                            <div className="flex items-center justify-start gap-firstDegree">
                                <Label
                                    size="h3"
                                    fontWeight="bold"
                                    message="consultants.statement.overview.overview"
                                />
                                {loading && <span className="loader" />}
                            </div>

                            <ListOfEditableOverview
                                year={selectedYear}
                                yearData={yearData}
                                userActive={user}
                            />
                        </div>

                        <Separator orientation="vertical" />

                        <div className="grow  flex flex-col gap-thirdDegree">

                            <div className="flex flex-col items-start w-full gap-secondDegree">
                                <Label
                                    size="h3"
                                    fontWeight="bold"
                                    message="consultants.statement.overflow.recap"
                                ></Label>

                                {loading && <span className="loader" />}
                                {error && <PrintErrorMessage message={error} />}
                                <InterestsHoldActiveWarning hold={holdsState.activeHold} className="w-full" />
                                <StatementTable
                                    data={yearData}
                                    dataOwner year={selectedYear} interestsHolds={holdsState.holds}
                                    onRefreshMonth={canManageStatement && !refreshProgress ? (month) => handleRefreshMonths([month]) : undefined}
                                    onProcessMonthInterests={canManageStatement && !processInterestsLoading ? (month) => handleProcessOverflowInterests(selectedYear, month) : undefined}
                                />

                            </div>

                            {/* <TableOverflow yearData={yearData} /> */}

                            {hasPrivileges("ADMIN_STATEMENT") && selectedYear && <div className="flex flex-col items-start w-full gap-thirdDegree">
                                <Label
                                    size="h3"
                                    fontWeight="bold"
                                    message="consultants.statement.overflow.overflowFluctuation"
                                />
                                <StatementOperationsChart year={selectedYear} yearStatement={yearData} dataOwner interestsHolds={holdsState.holds} />

                            </div>}
                            {/* inserer ici le graph  */}

                            {/* <div className="w-[100%]  max-h-[250px] h-full  bg-lighter"> */}
                            {/* <LineChartComp yearData={yearData} /> */}
                            {/* </div> */}
                        </div>
                    </div>
                )}

                {canManageStatement && (
                    <>
                        <RefreshStatementDialog
                            open={openRefreshDialog}
                            onOpenChange={setOpenRefreshDialog}
                            year={selectedYear}
                            onConfirm={handleRefreshMonths}
                            loading={Boolean(refreshProgress)}
                            progress={refreshProgress}
                        />
                        <ProcessOverflowInterestsDialog
                            open={openInterestsDialog}
                            onOpenChange={setOpenInterestsDialog}
                            years={allYears}
                            defaultYear={selectedYear}
                            onConfirm={handleProcessOverflowInterests}
                            loading={processInterestsLoading}
                            error={processInterestsError}
                        />
                    </>
                )}

                <Toaster />
            </div>

            :
            <InvoicesTab setInvoiceTab={setInvoiceTab} allYears={allYears} selectedYearParent={selectedYear} dataOwner={hasPrivileges("ADMIN_INVOICE")} />
    );
}
